import Cookies from 'js-cookie';

import { getBooleanConfig } from '@zg-rentals/frontend-config/client';

import {
  ZRM_AI_LISTING_CREATION_ENABLED,
  ZRM_FLEXIBLE_PROPERTIES_ENABLED,
  ZRM_MULTIPLEX_LISTING_FLOW_ENABLED,
  ZRM_TOTAL_MONTHLY_PRICE_ENABLED,
  ZRM_USER_MANAGEMENT_ENABLED,
  ZRM_USER_ONBOARDING_ENABLED,
} from '../constants/FeatureFlagConstants';
import SplitTestConstants from '../constants/SplitTestConstants';
import { USER_ONBOARDING_DISMISSED_KEY } from '../constants/UserOnboardingConstants';

import { getCookieAsBoolean } from './cookieUtils';
import propertyUtils from './propertyUtils';

import type { CometUserRoleType } from '@zg-rentals/rental-manager-env';
import type { SplitExperimentsProps } from '@zg-rentals/split';
import type {
  GetUserPropertyDetailsListingTypeWebDto,
  GetUserPropertySpotlightOverviewResponseDto,
  RentalManagerAuthCheckUserInfoWebApiDto,
} from '@zg-rentals/ts-rental-manager-api-client';

const canAccessModularListing = ({
  isMultifamily,
  listingTypeCode,
  lastActivatedMs,
  userCanAccessModularListing,
}: {
  isMultifamily: boolean;
  listingTypeCode: number;
  lastActivatedMs: number | undefined;
  userCanAccessModularListing: boolean;
}): boolean => {
  const hasListingBeenActivated = Boolean(lastActivatedMs);
  const isSingleFamilyRental = !isMultifamily && listingTypeCode === 1;
  const isRoomForRent = listingTypeCode === 5;

  return !hasListingBeenActivated && (isSingleFamilyRental || isRoomForRent) && userCanAccessModularListing;
};

const canAccessTopOfFunnelPremium = ({
  isFreemiumEnabled,
  isTopOfFunnelEnrollmentEligible,
  isActiveProductTypePremium,
}: {
  isFreemiumEnabled: boolean;
  isTopOfFunnelEnrollmentEligible: boolean;
  isActiveProductTypePremium: boolean;
}): boolean => {
  return isFreemiumEnabled && (isActiveProductTypePremium || isTopOfFunnelEnrollmentEligible);
};

const canAccessMLReactivationFlow = ({
  lastActivatedMs,
  isFeed,
  isMultifamily,
}: {
  lastActivatedMs?: number;
  isFeed?: boolean;
  isMultifamily?: boolean;
}) => {
  return Boolean((lastActivatedMs || isFeed) && !isMultifamily);
};

const canAccessSFCostsAndFees = ({ isFeed, isMultifamily }: { isFeed: boolean; isMultifamily: boolean }) =>
  !isFeed && !isMultifamily;

const canAccessFastPost = ({
  isMultifamily,
  lastActivatedMs,
  isFeed,
  restrictions,
}: {
  isMultifamily: boolean;
  lastActivatedMs: number | undefined;
  isFeed: boolean;
  restrictions: { applications: { isAccessRestricted: boolean } };
}) => {
  return !isMultifamily && !lastActivatedMs && !isFeed && !restrictions?.applications?.isAccessRestricted;
};

const canAccessPostingPathTourScheduling = ({
  isFeed,
  isInReactivationFlow,
  canAccessRelisting,
  active,
}: {
  isFeed: boolean;
  isInReactivationFlow: boolean;
  canAccessRelisting: boolean;
  active: boolean;
}) => {
  return Boolean(!isFeed && !(isInReactivationFlow && !canAccessRelisting && !active));
};

const canAccessRelistingFlow = ({ canBeRelisted }: { canBeRelisted: boolean }) => {
  return canBeRelisted;
};

const canAccessListingPreview = ({
  active,
  isFeed,
  isMultifamily,
  listingType,
}: Partial<{
  active: boolean;
  isFeed: boolean;
  isMultifamily: boolean;
  listingType: GetUserPropertyDetailsListingTypeWebDto;
}>) => {
  const isRoomForRent = propertyUtils.isRoomForRent({ listing: { listingType } });

  return !active && !isRoomForRent && !isFeed && !isMultifamily;
};

const canAccessTotalMonthlyPriceWithFee = ({ isEligible = false }: { isEligible?: boolean }) =>
  getCookieAsBoolean(ZRM_TOTAL_MONTHLY_PRICE_ENABLED) || isEligible;

const isTourSchedulingEnabledForProperty = ({
  active,
  canBeRelisted,
  isFeed,
  isInReactivationFlow,
}: {
  active: boolean;
  canBeRelisted: boolean;
  isFeed: boolean;
  isInReactivationFlow: boolean;
}) => {
  const canAccessRelisting = canAccessRelistingFlow({ canBeRelisted });
  return Boolean(!isFeed && !(isInReactivationFlow && !canAccessRelisting && !active));
};

const canAccessUserOnboardingExistingUser = ({ user }: { user: RentalManagerAuthCheckUserInfoWebApiDto | null }) => {
  if (!user) return false;

  if (!user?.restrictions?.isAllowed) {
    return false;
  }

  if (typeof window !== 'undefined' && sessionStorage.getItem(USER_ONBOARDING_DISMISSED_KEY) === 'true') {
    return false;
  }

  const hasListings = (user?.summary?.totalListingCount ?? 0) > 0;

  if (getCookieAsBoolean(ZRM_USER_ONBOARDING_ENABLED)) {
    return hasListings;
  }

  return Boolean(
    hasListings &&
    (!user.firstName ||
      !user.lastName ||
      !user.profiles?.profileType ||
      !user.profiles?.propertyCountRange ||
      !user?.firstName?.trim() ||
      !user?.lastName?.trim()),
  );
};

const canAccessUserOnboardingNewUser = ({
  user,
}: {
  user: {
    restrictions?: { isAllowed: boolean };
    features: { canViewOnboardingFlow: boolean };
  } | null;
}) => {
  if (user?.restrictions?.isAllowed && user.features.canViewOnboardingFlow) {
    return true;
  }

  return false;
};

const canAccessMultiplexListingFlow = ({
  canAccessMultiplexListingFlowResponse,
}: {
  canAccessMultiplexListingFlowResponse: boolean;
}) => {
  const areRequiredConditionsSatisfied = canAccessMultiplexListingFlowResponse === true;
  const areOverrideConditionsSatisfied = getCookieAsBoolean(ZRM_MULTIPLEX_LISTING_FLOW_ENABLED);

  return areRequiredConditionsSatisfied || areOverrideConditionsSatisfied;
};

const shouldShowBrokerFeeAlert = ({ state }: { state: string }) => {
  return state ? state === 'MA' : false;
};

const canFeedConnectSwitchSource = ({ user }: { user: { features: { canSwitchListingSource: boolean } } | null }) =>
  user?.features?.canSwitchListingSource;

const canAccessAddFeedListingsModal = ({ user }: { user: { features: { canViewClaimFeedListing: boolean } } | null }) =>
  user?.features?.canViewClaimFeedListing;

const canAccessFeatureNotifications = ({
  user,
}: {
  user: { features: { canAccessFeatureLaunchNotifications: boolean } } | null;
}) => user?.features?.canAccessFeatureLaunchNotifications;

const isNonAdvertiser = ({ user }: { user: { roleTypes: Array<CometUserRoleType> } | null }) =>
  Boolean(user?.roleTypes?.includes('NON_ADVERTISER_CUSTOMER' as CometUserRoleType));

const canAccessClaimPath = ({
  cookie,
  hasAnyPropertiesToClaim,
  isScreenMobile,
  isNonAdvertiser: isNonAdvertiserValue,
}: {
  cookie?: string;
  hasAnyPropertiesToClaim: boolean;
  isScreenMobile: boolean;
  isNonAdvertiser: boolean;
}) =>
  !isScreenMobile && (hasAnyPropertiesToClaim || isNonAdvertiserValue) && (!cookie || Cookies.get(cookie) !== 'true');

const canAccessClaimMorePath = ({
  isScreenMobile,
  hasAnyClaimedListings,
  hasNewPropertiesToClaim,
  cookie,
}: {
  isScreenMobile: boolean;
  hasAnyClaimedListings: boolean;
  hasNewPropertiesToClaim: boolean;
  cookie: string;
}) =>
  !isScreenMobile && hasAnyClaimedListings && hasNewPropertiesToClaim && (!cookie || Cookies.get(cookie) !== 'true');

const canAccessSpotlightListingsFirstUseModal = ({
  isScreenMobile,
  isSpotlightListingsEligible,
  cookie,
}: {
  isScreenMobile: boolean;
  isSpotlightListingsEligible: boolean;
  appConfig?: unknown;
  cookieEnabledServices?: unknown;
  cookie?: string;
}) => Boolean(!isScreenMobile && isSpotlightListingsEligible && (!cookie || Cookies.get(cookie) !== 'true'));

const canAccessListingSpotlightFilter = ({
  user,
  spotlightOverview,
}: {
  user: RentalManagerAuthCheckUserInfoWebApiDto | null;
  spotlightOverview?: GetUserPropertySpotlightOverviewResponseDto | null;
}) => Boolean(user?.isSingleFamilyEnterpriseCustomer && spotlightOverview?.isEligible);

const canAccessPortfolioSection = (): boolean => {
  return getBooleanConfig('canAccessUserManagement', false) || getCookieAsBoolean(ZRM_USER_MANAGEMENT_ENABLED);
};

const canAccessFlexibleProperties = (): boolean => {
  const requiredConditionsSatisfied = getBooleanConfig('canAccessFlexibleProperties', false);
  const overrideConditionsSatisfied = getCookieAsBoolean(ZRM_FLEXIBLE_PROPERTIES_ENABLED);

  return requiredConditionsSatisfied || overrideConditionsSatisfied;
};

type PossibleFeatureName = (typeof SplitTestConstants)[keyof typeof SplitTestConstants];
const getSplitExperimentTreatment = (
  splitExperiments: SplitExperimentsProps = {},
  featureName: PossibleFeatureName | '' = '',
) => splitExperiments?.[featureName]?.treatment === 'on';

const canAccessAIListingCreation = ({ splitExperiments }: { splitExperiments?: SplitExperimentsProps }): boolean =>
  getSplitExperimentTreatment(splitExperiments, SplitTestConstants.ZRM_AI_LISTING_CREATION) ||
  getCookieAsBoolean(ZRM_AI_LISTING_CREATION_ENABLED);

export default {
  canAccessModularListing,
  canAccessTopOfFunnelPremium,
  canAccessMLReactivationFlow,
  canAccessSFCostsAndFees,
  canAccessFastPost,
  canAccessPostingPathTourScheduling,
  canAccessTotalMonthlyPriceWithFee,
  canAccessMultiplexListingFlow,
  canAccessRelistingFlow,
  canAccessListingPreview,
  canAccessUserOnboardingNewUser,
  canAccessUserOnboardingExistingUser,
  isTourSchedulingEnabledForProperty,
  shouldShowBrokerFeeAlert,
  canFeedConnectSwitchSource,
  canAccessAddFeedListingsModal,
  canAccessFeatureNotifications,
  isNonAdvertiser,
  canAccessClaimPath,
  canAccessClaimMorePath,
  canAccessSpotlightListingsFirstUseModal,
  canAccessListingSpotlightFilter,
  canAccessPortfolioSection,
  canAccessFlexibleProperties,
  canAccessAIListingCreation,
};
